import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { TrendingDown, Zap, ClipboardList, CheckCircle, Layers } from 'lucide-react';
import { Reveal, Stagger, StaggerItem, EASE } from './LandingMotion';

const BENEFITS = [
  { icon: Zap, title: 'Answers in seconds', desc: 'No trips to the trailer or digging through binders.' },
  { icon: ClipboardList, title: 'Plans in minutes', desc: 'Bolt drafts the daily plan from your docs and roster.' },
  { icon: CheckCircle, title: 'Less rework', desc: 'Cited, current answers mean fewer wrong calls in the field.' },
  { icon: Layers, title: 'One source of truth', desc: 'Docs, RFIs, logs, and the schedule — no version chaos.' },
];

export const ImpactSection = () => {
  const reduce = useReducedMotion();
  return (
    <section className="py-24 md:py-32 relative bg-white">
      <div className="w-full max-w-[1200px] mx-auto px-6">

        <Reveal blur className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#1C1C1E] mb-6">
            Less busywork. <span className="text-[#FF6A00]">More building.</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed font-light">
            Virtual Hardhat takes the chase-and-paperwork off your crew’s plate — so the
            hours go back into the work that actually moves the job.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* Card 1: Time-on-paperwork (illustrative) */}
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: reduce ? 0.2 : 0.7, ease: EASE }}
            className="bg-white rounded-3xl vh-edge-light p-8 relative overflow-hidden"
          >
            <div className="flex justify-between items-start mb-10">
              <div>
                <h3 className="text-xl font-bold text-[#1C1C1E] mb-1">Time lost to paperwork</h3>
                <p className="text-sm text-slate-600">Shrinks week over week as Bolt takes it on</p>
              </div>
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, x: 12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: reduce ? 0.2 : 0.5, ease: EASE, delay: reduce ? 0 : 0.45 }}
                className="bg-[#FF6A00]/10 border border-[#FF6A00]/20 text-[#E25C00] px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1"
              >
                <TrendingDown size={16} /> Trending down
              </motion.div>
            </div>

            {/* Bar chart */}
            <div className="h-64 w-full relative flex items-end justify-between gap-3 border-b border-black/[0.06]">
              <div className="absolute inset-x-0 top-0 bottom-7 flex flex-col justify-between pointer-events-none">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="w-full h-px bg-black/[0.04]" />
                ))}
              </div>
              {[80, 72, 63, 50, 41, 33].map((h, i) => (
                <div key={i} className="flex-1 h-full flex flex-col justify-end items-center gap-3 relative z-10">
                  <motion.div
                    initial={{ height: 0 }}
                    whileInView={{ height: `${Math.round(h * 1.9)}px` }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: reduce ? 0 : 0.9, delay: reduce ? 0 : 0.25 + i * 0.08, ease: EASE }}
                    className="w-full rounded-t-lg bg-gradient-to-t from-[#FF6A00]/30 to-[#FF6A00]"
                  />
                  <span className="text-xs text-slate-500 font-mono">Wk {i + 1}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Grid of benefit cards (qualitative — no fabricated stats) */}
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {BENEFITS.map((item) => (
              <StaggerItem
                key={item.title}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.2, ease: EASE }}
                className="bg-white vh-edge-light rounded-3xl p-6 flex flex-col justify-between hover:border-black/15 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-[#FF6A00]/10 border border-[#FF6A00]/20 flex items-center justify-center mb-6">
                  <item.icon className="text-[#FF6A00]" size={22} />
                </div>
                <div>
                  <div className="text-[#1C1C1E] font-bold text-base mb-1">{item.title}</div>
                  <div className="text-slate-600 text-sm leading-relaxed">{item.desc}</div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

        </div>
      </div>
    </section>
  );
};
